import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import type { FeynmanView } from '../../shared/schemas.ts';
import type { LatLon } from '../../src/domain/geo.ts';
import { canGps, gpsAllowed, locate, type Place, reverse, search } from '../geo.ts';
import { fail, initials, toast, toastMsg } from '../lib.ts';

export function Toast() {
  return (
    <div id="toast" role="status" class={toastMsg.value ? 'show' : ''}>
      {toastMsg.value}
    </div>
  );
}

export const Spinner = () => <span class="spinner" aria-hidden="true" />;

export function Avatar({ name, color, small }: { name: string; color: string; small?: boolean }) {
  return (
    <span class={`avatar${small ? ' sm' : ''}`} style={{ background: color }} title={name}>
      {initials(name)}
    </span>
  );
}

export function Chips<T extends string>({
  options,
  value,
  onChange,
  small,
}: {
  options: { id: T; label: string; emoji?: string }[];
  value: T | null;
  onChange: (v: T) => void;
  small?: boolean;
}) {
  return (
    <div class="chips">
      {options.map((o) => (
        <button
          type="button"
          key={o.id}
          class={`chip${small ? ' sm' : ''}`}
          aria-pressed={o.id === value}
          onClick={() => onChange(o.id)}
        >
          {o.emoji && <span>{o.emoji}</span>} {o.label}
        </button>
      ))}
    </div>
  );
}

export function Card({ children, class: cls = '' }: { children: ComponentChildren; class?: string }) {
  return <section class={`card ${cls}`}>{children}</section>;
}

const pct = (x: number) => `${Math.round(x * 100)}%`;

/** Feynman's restaurant problem: go back to the best place, or keep exploring. */
export function FeynmanNote({ f }: { f: FeynmanView }) {
  const left = `${f.n} more meetup${f.n === 1 ? '' : 's'}`;
  return (
    <p
      class="feynman small"
      title="Feynman's restaurant rule: keep trying new places until one beats a bar that drops as fewer meetups are left, then stick with it."
    >
      🧭{' '}
      {f.stance === 'exploit' ? (
        <>
          <b>Go back to {f.best.name}.</b> {pct(f.best.value)} would go again, above the {pct(f.threshold)} bar for{' '}
          {left}.
        </>
      ) : (
        <>
          <b>Try somewhere new.</b> Your best so far, {f.best.name} ({pct(f.best.value)}), is below the{' '}
          {pct(f.threshold)} bar for {left}.
        </>
      )}
    </p>
  );
}

/** Photon-backed search with keyboard navigation. */
export function PlaceSearch({
  placeholder,
  near,
  onPick,
}: {
  placeholder: string;
  near?: LatLon | null;
  onPick: (p: Place) => void;
}) {
  const listId = useRef(`ac-${Math.random().toString(36).slice(2, 8)}`).current;
  const q = useSignal('');
  const items = useSignal<Place[]>([]);
  const sel = useSignal(-1);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const run = (text: string) => {
    q.value = text;
    clearTimeout(timer.current);
    if (text.trim().length < 2) {
      items.value = [];
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        items.value = await search(text.trim(), near);
        sel.value = -1;
      } catch (e) {
        fail(e);
      }
    }, 220);
  };
  const pick = (p: Place) => {
    q.value = '';
    items.value = [];
    onPick({ ...p, source: 'address' });
  };
  return (
    <div class="ac">
      <input
        type="search"
        role="combobox"
        aria-expanded={items.value.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={sel.value >= 0 ? `${listId}-${sel.value}` : undefined}
        placeholder={placeholder}
        value={q.value}
        autoComplete="off"
        onInput={(e) => run(e.currentTarget.value)}
        onBlur={() => setTimeout(() => (items.value = []), 150)}
        onKeyDown={(e) => {
          if (!items.value.length) return;
          if (e.key === 'ArrowDown') sel.value = Math.min(items.value.length - 1, sel.value + 1);
          else if (e.key === 'ArrowUp') sel.value = Math.max(0, sel.value - 1);
          else if (e.key === 'Enter') pick(items.value[Math.max(0, sel.value)]);
          else if (e.key === 'Escape') items.value = [];
          else return;
          e.preventDefault();
        }}
      />
      {items.value.length > 0 && (
        <div class="ac-list" role="listbox" id={listId}>
          {items.value.map((it, i) => {
            const [first, ...rest] = it.label.split(', ');
            return (
              <div
                key={`${it.lat},${it.lon}`}
                id={`${listId}-${i}`}
                role="option"
                tabIndex={-1}
                aria-selected={i === sel.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(it);
                }}
              >
                {first}
                <small>{rest.join(', ')}</small>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "Use my current location". With `auto`, it fills in silently when permission was already granted. */
export function GpsButton({
  onLocated,
  label = '📍 Use my current location',
  class: cls = 'btn primary big',
  auto,
}: {
  onLocated: (p: Place) => void | Promise<void>;
  label?: string;
  class?: string;
  auto?: boolean;
}) {
  const busy = useSignal(false);
  const run = async () => {
    busy.value = true;
    try {
      const p = await locate();
      await onLocated({ ...(await reverse(p)), accuracy: p.accuracy, source: 'gps' });
    } catch (e) {
      fail(e);
    }
    busy.value = false;
  };
  useEffect(() => {
    if (!auto) return;
    gpsAllowed().then((ok) => {
      if (ok) run();
    });
  }, []);
  if (!canGps()) {
    return (
      <span class="hint">GPS needs the https address of this site. You can still type an address or tap the map.</span>
    );
  }
  return (
    <button type="button" class={cls} disabled={busy.value} onClick={run}>
      {busy.value ? 'Locating…' : label}
    </button>
  );
}

export function ShareButton({ url, title, text }: { url: string; title: string; text: string }) {
  return (
    <button
      type="button"
      class="btn primary"
      onClick={async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title, text, url });
            return;
          } catch {
            /* cancelled: fall back to copy */
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          toast('Link copied');
        } catch {
          prompt('Copy this link', url);
        }
      }}
    >
      Share
    </button>
  );
}

/** Bottom sheet for map taps. */
export function Sheet({
  open,
  onClose,
  children,
  title,
}: {
  open: boolean;
  onClose: () => void;
  children: ComponentChildren;
  title?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);
  if (!open) return null;
  return (
    <div class="sheet-backdrop" onClick={onClose}>
      <div class="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div class="sheet-grab" />
        {title && <h3>{title}</h3>}
        {children}
      </div>
    </div>
  );
}
